import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import './NewsDetails.css';

const NewsDetails = () => {
  const { id } = useParams();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchNewsDetails();
  }, [id]);

  const fetchNewsDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/news/${id}`);

      if (!response.ok) {
        throw new Error('News not found');
      }

      const data = await response.json();
      setNews(data);

      // Fetch related news
      const relatedResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/news`);
      const allNews = await relatedResponse.json();
      const related = allNews
        .filter(
          (n) => n.id !== parseInt(id) && n.status === 'published' && n.category === data.category
        )
        .slice(0, 3);
      setRelatedNews(related);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const shareOnSocial = (platform) => {
    const url = window.location.href;
    const title = news?.title || '';

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + ' ' + url)}`,
    };

    window.open(shareUrls[platform], '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className='news-details-loading'>
        <div className='loading-spinner'></div>
        <p>Loading news...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='news-details-error'>
        <div className='error-icon'>❌</div>
        <h2>News Not Found</h2>
        <p>{error}</p>
        <Link to='/news' className='back-to-news-btn'>
          ← Back to News
        </Link>
      </div>
    );
  }

  if (!news) {
    return null;
  }

  return (
    <div className='news-details'>
      {/* Breadcrumb */}
      <section className='news-breadcrumb'>
        <div className='news-details-container'>
          <nav className='breadcrumb-nav'>
            <Link to='/' className='breadcrumb-link'>
              Home
            </Link>
            <span className='breadcrumb-separator'>›</span>
            <Link to='/news' className='breadcrumb-link'>
              News
            </Link>
            <span className='breadcrumb-separator'>›</span>
            <span className='breadcrumb-current'>{news.title}</span>
          </nav>
        </div>
      </section>

      {/* Hero Section */}
      <section className='news-hero'>
        <div className='news-details-container'>
          <div className='news-hero-content'>
            <div className='news-meta-header'>
              {news.category && <span className='news-category-badge'>{news.category}</span>}
              <div className='news-meta-info'>
                <span className='news-date'>
                  <i className='date-icon'>📅</i>
                  {formatDate(news.published_date || news.created_at)}
                </span>
                <span className='news-author'>
                  <i className='author-icon'>👤</i>
                  {news.author}
                </span>
              </div>
            </div>

            <h1 className='news-title'>{news.title}</h1>

            {news.summary && <p className='news-summary'>{news.summary}</p>}

            <div className='news-share'>
              <span className='share-label'>Share:</span>
              <div className='share-buttons'>
                <button onClick={() => shareOnSocial('facebook')} className='share-btn facebook'>
                  <i className='share-icon'>📘</i>
                </button>
                <button onClick={() => shareOnSocial('twitter')} className='share-btn twitter'>
                  <i className='share-icon'>🐦</i>
                </button>
                <button onClick={() => shareOnSocial('linkedin')} className='share-btn linkedin'>
                  <i className='share-icon'>💼</i>
                </button>
                <button onClick={() => shareOnSocial('whatsapp')} className='share-btn whatsapp'>
                  <i className='share-icon'>💬</i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className='news-content-section'>
        <div className='news-details-container'>
          <div className='news-layout'>
            <main className='news-main-content'>
              {news.featured_image && (
                <div className='news-featured-image'>
                  <img
                    src={`${process.env.REACT_APP_API_URL}${news.featured_image}`}
                    alt={news.title}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              <div className='news-content' dangerouslySetInnerHTML={{ __html: news.content }} />

              <div className='news-actions'>
                <div className='news-share-bottom'>
                  <h4>Share this news:</h4>
                  <div className='share-buttons'>
                    <button
                      onClick={() => shareOnSocial('facebook')}
                      className='share-btn facebook'
                    >
                      <i className='share-icon'>📘</i>
                      Facebook
                    </button>
                    <button onClick={() => shareOnSocial('twitter')} className='share-btn twitter'>
                      <i className='share-icon'>🐦</i>
                      Twitter
                    </button>
                    <button
                      onClick={() => shareOnSocial('linkedin')}
                      className='share-btn linkedin'
                    >
                      <i className='share-icon'>💼</i>
                      LinkedIn
                    </button>
                    <button
                      onClick={() => shareOnSocial('whatsapp')}
                      className='share-btn whatsapp'
                    >
                      <i className='share-icon'>💬</i>
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </main>

            <aside className='news-sidebar'>
              <div className='sidebar-widget'>
                <h3>Breaking News</h3>
                <div className='breaking-news-ticker'>
                  <p>
                    📢 Stay updated with the latest education news and announcements from around the
                    world.
                  </p>
                </div>
              </div>

              <div className='sidebar-widget'>
                <h3>Quick Contact</h3>
                <div className='contact-info'>
                  <div className='contact-item'>
                    <i className='contact-icon'>📞</i>
                    <span>+971 581236700</span>
                  </div>
                  <div className='contact-item'>
                    <i className='contact-icon'>✉️</i>
                    <span>admin@destnation.org</span>
                  </div>
                </div>
                <Link to='/contact' className='contact-btn'>
                  Get Free Consultation
                </Link>
              </div>

              <div className='sidebar-widget'>
                <h3>Newsletter</h3>
                <p>Subscribe to get the latest news delivered to your inbox.</p>
                <div className='newsletter-form'>
                  <input type='email' placeholder='Your email' className='newsletter-input' />
                  <button className='newsletter-btn'>Subscribe</button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Related News */}
      {relatedNews.length > 0 && (
        <section className='related-news'>
          <div className='news-details-container'>
            <h2>Related News</h2>
            <div className='related-news-grid'>
              {relatedNews.map((relatedNewsItem) => (
                <article key={relatedNewsItem.id} className='related-news-card'>
                  <div className='related-news-image'>
                    {relatedNewsItem.featured_image ? (
                      <img
                        src={`${process.env.REACT_APP_API_URL}${relatedNewsItem.featured_image}`}
                        alt={relatedNewsItem.title}
                        onError={(e) => {
                          e.target.src = '/assets/img/placeholder-news.jpg';
                        }}
                      />
                    ) : (
                      <div className='related-news-placeholder'>
                        <i className='placeholder-icon'>📰</i>
                      </div>
                    )}
                    {relatedNewsItem.category && (
                      <span className='news-category'>{relatedNewsItem.category}</span>
                    )}
                  </div>
                  <div className='related-news-content'>
                    <div className='news-meta'>
                      <span className='news-date'>
                        <i className='date-icon'>📅</i>
                        {formatDate(relatedNewsItem.published_date || relatedNewsItem.created_at)}
                      </span>
                      <span className='news-author'>
                        <i className='author-icon'>👤</i>
                        {relatedNewsItem.author}
                      </span>
                    </div>
                    <h3>
                      <Link to={`/news/${relatedNewsItem.id}`}>{relatedNewsItem.title}</Link>
                    </h3>
                    <p>
                      {relatedNewsItem.summary?.substring(0, 100) ||
                        relatedNewsItem.content.replace(/<[^>]*>/g, '').substring(0, 100)}
                      ...
                    </p>
                    <Link to={`/news/${relatedNewsItem.id}`} className='read-more-btn'>
                      Read More
                      <i className='arrow-icon'>→</i>
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className='news-cta'>
        <div className='news-details-container'>
          <div className='cta-content'>
            <h2>Need Expert Guidance for Your Education Journey?</h2>
            <p>
              Our experienced counselors are here to help you make informed decisions about your
              international education
            </p>
            <div className='cta-buttons'>
              <Link to='/contact' className='cta-btn primary'>
                <i className='cta-icon'>📞</i>
                Contact Us
              </Link>
              <Link to='/news' className='cta-btn secondary'>
                <i className='cta-icon'>📰</i>
                More News
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetails;
